import { Button, Col, Row } from 'react-bootstrap';
import styled, { css } from 'styled-components';
import { BsXCircle, BsXLg } from 'react-icons/bs';
import LocationFilter from './LocationFilter';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters } from '../../../redux/slices/filters';
import DeviceFilter from './DeviceFilter';
import DateFilter from './DateFilter';
import moment from 'moment';
import { useEffect } from 'react';
import QrFilter from './QrFilter';
import { useTranslation } from 'react-i18next';
import useUrlParams from '../../../hooks/useUrlParams';
import Loader from '../../loader';
import RegisterBlock from './RegisterBlock';
import PaginationMeQr from '../pagination/PaginationMeQr';
import { setFolderId } from '../../../redux/slices/meQrData';
import useRequestLocation from '../../../hooks/useRequestLocation';
import PdfLoader from '../../loader/PdfLoader';
import ExportReport from './exportReport';
import useResetFilters from '../../../hooks/useResetFilters';

const START_DATE_RANGE = {
  selection1: {
    startDate: moment().subtract(1, 'week').toDate(),
    endDate: moment().toDate(),
    key: 'selection1'
  }
};

const Filters = ({ withParentConfirmation, isLinkClicksTab = false, linkClicks = [] }) => {
  const { isFitersActive, maxPage, appliedFilters } = useSelector((state) => ({
    isFitersActive: state.filters.isFitersActive,
    maxPage: state.meQrData.pagination.maxPage,
    appliedFilters: state.filters
  }));
  const { t, i18n } = useTranslation();
  const { noUser, isMePage, isGenQrCodePage } = useUrlParams();
  const dispatch = useDispatch();
  const resetAllFilters = useResetFilters();

  const [selectedQrs, setSelectedQrs] = useState([]);
  const [selectedPageLinksCount, setSelectedPageLinksCount] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [dateRange, setDateRange] = useState(START_DATE_RANGE);
  const [showLoader, setShowLoader] = useState(false);
  const [oldestEntryDate, setOldestEntryDate] = useState(null);
  const { getOldestEntryDate } = useRequestLocation();

  useEffect(() => {
    getOldestEntryDate().then((date) => {
      if (date) setOldestEntryDate(date);
    });
  }, []);

  const handleApply = () => {
    if (
      !selectedQrs.length &&
      !selectedCountry &&
      !selectedCountry &&
      JSON.stringify(dateRange) === JSON.stringify(START_DATE_RANGE)
    ) {
      return;
    }
    const isModeCompare = !!dateRange?.selection2;
    const filterOptions = {
      qrIds: selectedQrs,
      selectedPageLinksCount,
      location: selectedCountry,
      device: selectedDevice,
      isCompareMode: isModeCompare,
      periodStart: dateRange.selection1.startDate
        ? moment(dateRange.selection1.startDate).startOf('D').toDate()
        : null,
      periodEnd: dateRange.selection1.endDate
        ? moment(dateRange.selection1.endDate).endOf('D').toDate()
        : null,
      secondPeriodStart: isModeCompare
        ? moment(dateRange.selection2.startDate).startOf('D').toDate()
        : null,
      secondPeriodEnd: isModeCompare
        ? moment(dateRange.selection2.endDate).endOf('D').toDate()
        : null,
      mode: isModeCompare ? 'compare' : 'period',
      isFitersActive: true
    };
    dispatch(setFilters(filterOptions));
    dispatch(setFolderId(selectedFolderId));
  };

  useEffect(() => {
    if (!isFitersActive) {
      setSelectedPageLinksCount(null);
      setSelectedDevice('');
      setSelectedCountry('');
      setDateRange(START_DATE_RANGE);
    }
  }, [isFitersActive]);

  const handleShowLoader = (isShow) => {
    setShowLoader(isShow);
  };

  return (
    <>
      {noUser ? <RegisterBlock /> : <Loader opacity={50} toggle={showLoader} />}
      <PdfLoader />
      <section>
        <div
          className={`
            bg-secondary${isMePage ? '2' : ''} p-3 p-lg-4 ${isMePage ? 'rounded-4 mb-4' : 'rounded'}
            ${isGenQrCodePage && ' bg-secondary-gen-qr'}
          `}
        >
          <Row>
            <Col>
              <div className="d-flex justify-content-between alignt-itens-center">
                <div className="fs-4 fw-bold position-relative me-3">
                  {t('filters.filters')}
                  {isFitersActive && (
                    <div
                      className={`position-absolute rounded-circle ${
                        isMePage ? 'bg-orange' : 'bg-primary'
                      }
                      ${isGenQrCodePage ? 'bg-primary-gen-qr' : 'bg-primary'}
                      `}
                      style={{ width: 10, height: 10, right: -10, top: 5 }}
                    />
                  )}
                </div>
                <div className="d-flex">
                  <ExportReport
                    handleShowLoader={handleShowLoader}
                    isFitersActive={isFitersActive}
                    withParentConfirmation={withParentConfirmation}
                    isLinkClicksTab={isLinkClicksTab}
                    linkClicks={linkClicks}
                  />

                  {isFitersActive && (
                    <div
                      className="text-dark d-flex align-items-center ms-sm-4 filter-action"
                      style={{ cursor: 'pointer' }}
                      onClick={resetAllFilters}
                    >
                      {isMePage ? (
                        <BsXLg className="me-2" />
                      ) : (
                        <BsXCircle className="me-2" />
                      )}
                      {t('filters.clear')}
                    </div>
                  )}
                </div>
              </div>

              {!isMePage && maxPage > 1 && <PaginationMeQr />}

              <Row className="gy-2 mt-1">
                <CustomCol xs={12}>
                  <QrFilter
                    selectedQrs={selectedQrs}
                    setSelectedQrs={setSelectedQrs}
                    setSelectedPageLinksCount={setSelectedPageLinksCount}
                    setSelectedFolderId={setSelectedFolderId}
                    onPageReset={() => {
                      if (appliedFilters.qrIds.length > 0) {
                        dispatch(setFilters({
                          ...appliedFilters,
                          qrIds: [],
                          selectedPageLinksCount: null
                        }));
                      }
                    }}
                  />
                </CustomCol>

                <CustomCol xs={12}>
                  <DateFilter
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    minDate={oldestEntryDate}
                    onApply={handleApply}
                  />
                </CustomCol>

                <CustomCol xs={12}>
                  <LocationFilter
                    selectedCountry={selectedCountry}
                    setSelectedCountry={setSelectedCountry}
                  />
                </CustomCol>

                <CustomCol xs={12}>
                  <DeviceFilter
                    selectedDevice={selectedDevice}
                    setSelectedDevice={setSelectedDevice}
                  />
                </CustomCol>

                <CustomCol xs={12}>
                  <CustomButton
                    hoverColor={isMePage ? '#357CEC' : null}
                    className={`w-100 fw-bold py-3 mt-2 mt-lg-0${
                      isMePage ? ' bg-primary2 border-primary2' : ''
                    }
                    ${isGenQrCodePage && ' bg-primary-gen-qr'}
                    `}
                    onClick={() => {
                      handleApply();
                      window.gtag("event", "apply_filters", {
                        site_language: i18n.language.toUpperCase(),
                        location: selectedCountry ? selectedCountry : "All Countries",
                        device: selectedDevice ? selectedDevice : "All Devices",
                      });
                    }}
                  >
                    {t('filters.applyFilters')}
                  </CustomButton>
                </CustomCol>
              </Row>
            </Col>
          </Row>
        </div>
      </section>
    </>
  );
};

export default Filters;

export const CustomCol = styled(Col)`
  @media (min-width: 992px) {
    width: 20%;
  }
`;

const CustomButton = styled(Button)`
  ${({ hoverColor }) =>
    hoverColor &&
    css`
      &:hover {
        background-color: ${hoverColor} !important;
      }
    `}
`;
